import { apiClient, APIError, registerTokenAccessor } from "./client";
import endpoint from "@/constants/endpoint";
import type {
  LoginPasswordRequest,
  OTPRequestPayload,
  OTPRequestResponse,
  OTPVerifyRequest,
  AuthUser,
  CreateUserRequest,
  VerifyInviteTokenResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
} from "@chinese-vendor/schemas";

/** Maps an API error to a stable i18n error key for the auth domain. */
export function mapAuthError(err: unknown): string {
  if (err instanceof APIError) {
    if (err.status === 401) return "auth.login.error.wrongPassword";
    if (err.status === 403) return "auth.login.error.invalidToken";
    if (err.status === 409) return "auth.register.error.invalidInvite";
    if (err.status === 422) return "auth.otp.error.invalid";
    if (err.status === 502) return "common.error.network";
  }
  return "common.error.generic";
}

export const authApi = {
  /** Mode A: Email + Password login — POST /api/v1/auth/login */
  async loginWithPassword(
    payload: LoginPasswordRequest
  ): Promise<{ token: string; user: AuthUser }> {
    const res = await apiClient.post<{ access_token: string }>(endpoint.auth.login, payload);
    const token = res.data.access_token;
    // Register token so the subsequent me() call can attach the Authorization header.
    registerTokenAccessor(() => token);
    const user = await authApi.me();
    return { token, user };
  },

  /** Mode B: Request email OTP — POST /api/v1/auth/otp/request. NO SMS per SPEC §6.1. */
  async requestOTP(payload: OTPRequestPayload): Promise<OTPRequestResponse> {
    const res = await apiClient.post<OTPRequestResponse>(endpoint.auth.otpRequest, payload);
    return res.data;
  },

  /** Mode B: Verify email OTP — POST /api/v1/auth/otp/verify */
  async loginWithOTP(payload: OTPVerifyRequest): Promise<{ token: string; user: AuthUser }> {
    const res = await apiClient.post<{ access_token: string }>(endpoint.auth.otpVerify, payload);
    const token = res.data.access_token;
    // Register token so the subsequent me() call can attach the Authorization header.
    registerTokenAccessor(() => token);
    const user = await authApi.me();
    return { token, user };
  },

  /** Register a new vendor user — POST /api/v1/users */
  async register(payload: CreateUserRequest): Promise<{ token: string; user: AuthUser }> {
    const res = await apiClient.post<{ access_token: string }>(endpoint.users.create, payload);
    const token = res.data.access_token;
    registerTokenAccessor(() => token);
    const user = await authApi.me();
    return { token, user };
  },

  /** Verify an invite token — POST /api/v1/auth/verify-invite-token */
  async verifyInviteToken(token: string): Promise<VerifyInviteTokenResponse> {
    const res = await apiClient.post<VerifyInviteTokenResponse>(endpoint.auth.verifyInviteToken, {
      token,
    });
    return res.data;
  },

  /** Get current authenticated user — GET /api/v1/auth/me */
  async me(): Promise<AuthUser> {
    const res = await apiClient.get<AuthUser>(endpoint.auth.me);
    return res.data;
  },

  /**
   * Invalidate the current session on the server — POST /api/v1/auth/logout.
   * Blacklists the access token (and refresh token if provided) in Redis so
   * they cannot be reused even before expiry.
   * Best-effort: if the backend is unreachable the caller should still clear
   * local state.
   */
  async logout(refreshToken?: string): Promise<void> {
    await apiClient.post(endpoint.auth.logout, { refresh_token: refreshToken ?? null });
  },

  /** Change password — POST /api/v1/auth/change-password */
  async changePassword(payload: ChangePasswordRequest): Promise<ChangePasswordResponse> {
    const res = await apiClient.post<ChangePasswordResponse>(endpoint.auth.changePassword, payload);
    return res.data;
  },

  /** Forgot password — request password-reset OTP — POST /api/v1/auth/forgot-password */
  async forgotPassword(payload: {
    email: string;
  }): Promise<{ otp_token: string; expires_in: number }> {
    const res = await apiClient.post<{ otp_token: string; expires_in: number }>(
      endpoint.auth.forgotPassword,
      payload
    );
    return res.data;
  },

  /** Reset password — verify OTP + set new password — POST /api/v1/auth/reset-password */
  async resetPassword(payload: {
    otp_token: string;
    otp_code: string;
    new_password: string;
  }): Promise<void> {
    await apiClient.post(endpoint.auth.resetPassword, payload);
  },
};
